<template>
    <b-form-group :label="label" label-for="owner">
        <Select
            id="owner"
            :state="state"
            :selected="value"
            :options="ownersOptions"
            @changeValue="$emit('update:value', $event)"
            aria-describedby="owner-feedback"
            :disabled="disabled"
            :internal-search="isInternalSearch"
            @changeSearch="debouncedSearchHandler"
            :loading="isLoading"
            :no-elements-found="$t('form.multiselect.noUserWasFound')"
        />
        <b-form-invalid-feedback id="owner-feedback">
            <slot />
        </b-form-invalid-feedback>
    </b-form-group>
</template>

<script>
import { Select, mapperToOption, mapperToOptions } from '@/views/components/selects'
import api from '@/services/api'
import { sortBy, debounce } from 'lodash'

export default {
    name: 'Owner',
    components: {
        Select,
    },
    props: ['value', 'state', 'disabled', 'isEditing', 'label', 'canAssign', 'moduleForm', 'defaultOwnerId'],
    data() {
        return {
            ownersOptions: [],
            defaultOwnersOptions: [],
            currentUser: null,
            isLoading: false,
        }
    },
    created() {
        this.debouncedSearchHandler = debounce(this.searchUserByName, 1000)
    },
    mounted() {
        this.getCurrentUserFromAPI()
    },
    methods: {
        searchUserByName(query) {
            if (this.isAdminOrTargetMaster && query) {
                this.isLoading = true
                let params = {
                    orderBy: 'name',
                    sortedBy: 'asc',
                    filter: 'id;name;picture',
                    and: 'yes',
                }
                switch (this.moduleForm) {
                    case 'target':
                        params = {
                            ...params,
                            search: `name:${query};has_goal:1`,
                        }
                        break
                    case 'indicator': {
                        params = {
                            ...params,
                            search: `name:${query};has_kpi:1`,
                        }
                        break
                    }
                }
                return api
                    .get('/users', { params })
                    .then((resp) => {
                        const {
                            data: { data },
                        } = resp
                        this.ownersOptions = mapperToOptions(data, undefined, 'name')
                    })
                    .catch((error) => {
                        console.log(error)
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            } else {
                this.ownersOptions = this.defaultOwnersOptions
            }
        },
        getCurrentUserFromAPI() {
            return api
                .get('/users/current', {
                    params: {
                        filter: 'id;name;picture;role_id;is_target_master',
                        with: 'role',
                    },
                })
                .then((resp) => {
                    this.currentUser = resp.data.data
                    this.getSubordinatesWithModuleFromAPI()
                })
                .catch((error) => {
                    console.log(error)
                })
        },
        getSubordinatesWithModuleFromAPI() {
            let params = {
                filter: 'id;name;picture;role_id',
                with: 'role',
                orderBy: 'name',
                sortedBy: 'asc',
                and: 'yes',
            }
            switch (this.moduleForm) {
                case 'target':
                    params = {
                        ...params,
                        search: `manager_id:${this.currentUser.id};has_goal:1`,
                    }
                    break
                case 'indicator':
                    params = {
                        ...params,
                        search: `manager_id:${this.currentUser.id};has_kpi:1`,
                    }
                    break
            }
            return api
                .get('/users', { params })
                .then((resp) => {
                    const {
                        data: { data },
                    } = resp
                    this.currentUser.subordinates = data
                    let defaultOwner = this.currentUser
                    if (!this.isEditing) {
                        if (this.defaultOwnerId) {
                            const userFound = this.isProfileOwnerSubordinatedOfCurrentUser(data)
                            if (userFound) {
                                defaultOwner = userFound
                            } else {
                                const params = {
                                    filter: 'id;name',
                                }
                                api.get(`/users/${this.defaultOwnerId}`, {
                                    params,
                                })
                                    .then((resp) => {
                                        let profileOwnerData = {
                                            ...resp.data.data,
                                            id: this.defaultOwnerId,
                                        }
                                        this.$emit('update:value', mapperToOption(profileOwnerData, undefined, 'name'))
                                    })
                                    .catch((e) => {
                                        console.error(e)
                                    })
                            }
                        }
                        this.$emit('update:value', mapperToOption(defaultOwner, undefined, 'name'))
                    }
                    let users = [this.currentUser]
                    if (this.currentUser.subordinates.length) {
                        users = sortBy([...this.currentUser.subordinates, this.currentUser], (user) => user.name)
                    }
                    this.defaultOwnersOptions = mapperToOptions(users, undefined, 'name')
                    if (this.canAssign) {
                        this.ownersOptions = this.defaultOwnersOptions
                    } else {
                        this.ownersOptions = mapperToOptions([this.currentUser], undefined, 'name')
                    }
                })
                .catch((error) => {
                    console.log(error)
                })
                .finally(() => {
                    this.$emit('ready', true)
                })
        },
        isProfileOwnerSubordinatedOfCurrentUser(subordinates) {
            let userFound = subordinates.find((user) => {
                return user.id == this.defaultOwnerId
            })
            return userFound
        },
    },
    computed: {
        isAdminOrTargetMaster() {
            if (!this.currentUser) {
                return false
            }

            const { role, is_target_master } = this.currentUser
            return role.name === 'administrator' || is_target_master
        },
        isInternalSearch() {
            return !this.isAdminOrTargetMaster
        },
    },
    watch: {
        canAssign(canAssign) {
            if (!canAssign) {
                this.ownersOptions = mapperToOptions([this.currentUser], undefined, 'name')
            } else {
                this.ownersOptions = this.defaultOwnersOptions
            }
        },
    },
}
</script>
